import { useInfiniteQuery } from '@tanstack/react-query';
import type { AllocationListItem } from '@billda/api';
import { unwrap } from '@billda/api';
import { api } from '../../../shared/api/billdaApi';
import { useAuthStore } from '../../auth/stores/authStore';

export const allocationsKeys = {
  all: ['allocations'] as const,
  lists: () => [...allocationsKeys.all, 'list'] as const,
  list: (
    siteId: number,
    plotId: number,
    labourItemId: number,
    isAuthenticated: boolean
  ) =>
    [
      ...allocationsKeys.lists(),
      { siteId, plotId, labourItemId, isAuthenticated },
    ] as const,
};

const ITEMS_PER_PAGE = 20;

export interface UseAllocationsQueryReturn {
  allocations: AllocationListItem[];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

/**
 * Hook to fetch allocations for a labour item with pagination support
 */
export function useAllocationsQuery(
  siteId: number,
  plotId: number,
  labourItemId: number,
  enabled: boolean = true
): UseAllocationsQueryReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: allocationsKeys.list(
      siteId,
      plotId,
      labourItemId,
      isAuthenticated
    ),
    queryFn: async ({ pageParam = 0, signal }) => {
      return unwrap(
        await api.legacy.GET(
          '/api/v2/sites/{siteId}/plots/{plotId}/labour-items/{labourItemId}/allocations',
          {
            params: {
              path: { siteId, plotId, labourItemId },
              query: { offset: pageParam, take: ITEMS_PER_PAGE },
            },
            signal,
          }
        )
      );
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.hasMore || !allPages) return undefined;
      return allPages.flatMap(p => p.items).length;
    },
    initialPageParam: 0,
    enabled:
      isAuthenticated &&
      siteId > 0 &&
      plotId > 0 &&
      labourItemId > 0 &&
      enabled,
    staleTime: 30000,
    placeholderData: previousData => previousData,
  });

  const allocations = data?.pages.flatMap(page => page?.items ?? []) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    allocations,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    loadMore: fetchNextPage,
  };
}
